export type CaptureSlot =
  // Sales-flow slots
  | 'name'
  | 'email'
  | 'organisation'
  | 'phone'
  | 'window'
  | 'topic'
  // Intake-flow slots
  | 'role'
  | 'current_state'
  | 'desired_state'
  | 'constraints'
  | 'success';

export type CaptureStatus = 'tentative' | 'confirmed';

export interface CaptureUpdate {
  slot: CaptureSlot;
  value: string;
  status: CaptureStatus;
}

const SLOT_LABELS: Record<CaptureSlot, string> = {
  // Sales
  name: 'Name',
  email: 'Email',
  organisation: 'Organisation',
  phone: 'Phone',
  window: 'Preferred window',
  topic: 'Topic',
  // Intake
  role: 'Role',
  current_state: 'Today',
  desired_state: 'Wanted',
  constraints: 'Must respect',
  success: 'Done looks like',
};

/** Order slots appear, top to bottom, on the card.
 *  Sales slots first; intake slots only render rows when populated, so
 *  appending them is harmless for sales sessions. */
const SLOT_ORDER: CaptureSlot[] = [
  // Sales
  'name',
  'email',
  'organisation',
  'phone',
  'window',
  'topic',
  // Intake (rendered when populated)
  'role',
  'current_state',
  'desired_state',
  'constraints',
  'success',
];

interface RowState {
  value: string;
  status: CaptureStatus;
}

/**
 * Capture card — Merlin's live "notebook" of caller details.
 *
 * The card is hidden until Merlin fires the first `update_capture` tool
 * call. Each slot has three visual states:
 *   · empty    (dimmed, italic placeholder)
 *   · tentative (italic amber, "hearing")
 *   · confirmed (solid white with a tick)
 *
 * When a confirmed value is updated (caller correction), the row flashes
 * red briefly then settles into the new value.
 */
export class CaptureCard {
  private readonly root: HTMLElement;
  private readonly rows: Partial<Record<CaptureSlot, HTMLElement>> = {};
  private readonly states: Partial<Record<CaptureSlot, RowState>> = {};

  constructor(root: HTMLElement) {
    this.root = root;
    this.render();
  }

  reset() {
    for (const slot of SLOT_ORDER) delete this.states[slot];
    this.root.classList.remove('visible');
    for (const slot of SLOT_ORDER) this.renderRow(slot);
  }

  apply(update: CaptureUpdate) {
    const prev = this.states[update.slot];
    const becameDifferent =
      prev && prev.status === 'confirmed' && prev.value !== update.value;

    this.states[update.slot] = { value: update.value, status: update.status };
    this.root.classList.add('visible');

    const row = this.rows[update.slot];
    if (!row) return;

    if (becameDifferent) {
      row.classList.add('corrected');
      // let the CSS animation finish before we rerender, so the red pulse shows
      window.setTimeout(() => {
        row.classList.remove('corrected');
        this.renderRow(update.slot);
      }, 260);
    } else {
      this.renderRow(update.slot);
    }
  }

  private render() {
    this.root.classList.add('capture-card');
    this.root.innerHTML = `
      <div class="capture-head">
        <div class="capture-label">Merlin's notebook</div>
        <div class="capture-sub">Live capture</div>
      </div>
      <div class="capture-body"></div>
    `;
    const body = this.root.querySelector<HTMLElement>('.capture-body');
    if (!body) return;

    for (const slot of SLOT_ORDER) {
      const row = document.createElement('div');
      row.className = 'capture-row empty';
      row.dataset.slot = slot;
      row.innerHTML = `
        <div class="capture-key">${SLOT_LABELS[slot]}</div>
        <div class="capture-value"></div>
        <div class="capture-tick" aria-hidden="true">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
            <polyline points="20 6 9 17 4 12"/>
          </svg>
        </div>
      `;
      body.appendChild(row);
      this.rows[slot] = row;
      this.renderRow(slot);
    }
  }

  private renderRow(slot: CaptureSlot) {
    const row = this.rows[slot];
    if (!row) return;
    const state = this.states[slot];
    const valueEl = row.querySelector<HTMLElement>('.capture-value');
    if (!valueEl) return;

    row.classList.remove('empty', 'tentative', 'confirmed');
    if (!state) {
      // Empty slots are hidden entirely (rather than rendered as a "—" row).
      // Keeps the card tidy when only some slots populate — important now
      // that we have 11 slots split across the sales / intake variants and
      // each variant only ever fills a subset.
      row.classList.add('empty');
      row.style.display = 'none';
      valueEl.textContent = '';
      return;
    }
    row.style.display = '';
    if (state.status === 'tentative') {
      row.classList.add('tentative');
    } else {
      row.classList.add('confirmed');
    }
    valueEl.textContent = state.value;
  }
}
